import { GridColDef, GridSortCellParams } from "@mui/x-data-grid-pro"
import { Box, Tooltip, Typography } from "@mui/material"
import { StudentAcademicStatusDTO } from "../../studentAcademicStatus.dto"
import { ColumnKeys } from "./column.keys"
import {
  CSAT_FLAG_MAX,
  NPS_FLAG_MAX,
  getAttentionSignals,
  SIGNAL_DEFINITIONS,
} from "../../studentFlags"
import { formatScoreAgeLong, isScoreFreshForFlag } from "../../scoreRecency"
import { numberSorter } from "./column.sort.comparators"

// The comparator and the cell need the full row (signals derive from several
// DTO fields), while the cell value stays the joined labels for search,
// quick-filter and export — the exported words are the words the cell shows.
function rowFromSortParams(
  params: GridSortCellParams,
): StudentAcademicStatusDTO | null {
  return params.api.getRow(params.id)
}

// Two lines, no chips (chips clipped mid-pill at any honest width):
//   CSAT 2 · NPS 6        <- compact scores, red while fresh-flagged
//   stalled 124d · 1 more <- the dominant signal, plain red text
// The tooltip carries every signal WITH its plain-language definition —
// "stalled" alone sent Mayer asking. The actionables live on the row itself
// (expand the snapshot, view-as); this column's job is triage order and
// explanation.
export const ATTENTION_COLUMN: GridColDef<StudentAcademicStatusDTO> = {
  field: ColumnKeys.ATTENTION,
  headerName: "Attention",
  description:
    "Why this student may need a look: fresh low scores, stalled progress, missed sessions. Sorted first by most signals.",
  flex: 1.2,
  minWidth: 150,
  valueGetter: (value, studentRow: StudentAcademicStatusDTO) =>
    getAttentionSignals(studentRow)
      .map((signal) => signal.label)
      .join(", "),
  // First header click sorts most-signals-first — the triage order.
  sortingOrder: ["desc", "asc", null],
  getSortComparator(sortDirection) {
    return (v1, v2, cellParams1, cellParams2) => {
      const row1 = rowFromSortParams(cellParams1)
      const row2 = rowFromSortParams(cellParams2)
      return numberSorter(
        row1 ? getAttentionSignals(row1).length : 0,
        row2 ? getAttentionSignals(row2).length : 0,
        sortDirection,
      )
    }
  },
  renderCell: (params) => {
    const row = params.row
    const signals = getAttentionSignals(row)
    const csat = row.status.satisfaction.csat
    const nps = row.status.satisfaction.nps
    const csatFlagged =
      !!csat &&
      csat.score <= CSAT_FLAG_MAX &&
      isScoreFreshForFlag(csat.submittedAt)
    const npsFlagged =
      !!nps && nps.score <= NPS_FLAG_MAX && isScoreFreshForFlag(nps.submittedAt)
    // The behavioral signals get the label line; the score signals already
    // live in the score line's colour, so repeating them as text would say
    // the same thing twice.
    const behavioral = signals.filter(
      (signal) => signal.key === "stall" || signal.key === "noSession",
    )
    if (!csat && !nps && signals.length === 0) return null
    const tooltip = (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {signals.length === 0 && (
          <Typography variant="body3" color="inherit">
            No attention signals
          </Typography>
        )}
        {signals.map((signal) => (
          <Typography key={signal.key} variant="body3" color="inherit">
            {signal.label} — {SIGNAL_DEFINITIONS[signal.key]}
          </Typography>
        ))}
        {csat && !csatFlagged && (
          <Typography variant="body3" color="inherit">
            CSAT {csat.score} · rated {formatScoreAgeLong(csat.submittedAt)}{" "}
            (not flagged)
          </Typography>
        )}
        {nps && !npsFlagged && (
          <Typography variant="body3" color="inherit">
            NPS {nps.score} · rated {formatScoreAgeLong(nps.submittedAt)} (not
            flagged)
          </Typography>
        )}
      </Box>
    )
    return (
      <Tooltip placement="right-end" title={tooltip}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            minWidth: 0,
          }}
        >
          {(csat || nps) && (
            <Typography noWrap display="block" variant="body3">
              {csat && (
                <Box
                  component="span"
                  sx={{
                    color: csatFlagged ? "error.main" : "text.secondary",
                  }}
                >
                  CSAT {csat.score}
                </Box>
              )}
              {csat && nps && (
                <Box component="span" sx={{ color: "text.disabled" }}>
                  {" · "}
                </Box>
              )}
              {nps && (
                <Box
                  component="span"
                  sx={{ color: npsFlagged ? "error.main" : "text.secondary" }}
                >
                  NPS {nps.score}
                </Box>
              )}
            </Typography>
          )}
          {behavioral.length > 0 && (
            <Typography
              noWrap
              display="block"
              variant="body3"
              sx={{ color: "error.main" }}
            >
              {behavioral[0].label}
              {behavioral.length > 1 && (
                <Box component="span" sx={{ color: "text.secondary" }}>
                  {` · ${behavioral.length - 1} more`}
                </Box>
              )}
            </Typography>
          )}
        </Box>
      </Tooltip>
    )
  },
}
